/// <amd-module name="Test/ConfigurationToolbar/ControllerViews/ConfigurationToolbar" />
import { CommandBar } from "OfficeFabric/CommandBar";
import { IContextualMenuItem } from "OfficeFabric/ContextualMenu";
import { IIconProps } from "OfficeFabric/Icon";
import { Label } from "OfficeFabric/Label";
import * as React from "react";
import * as Definitions from "Test/Analytics/Common/Definitions";
import * as CommonTypes from "Test/Analytics/Common/Types";
import { FilterState } from "Test/common-util/Contracts/Filters";
import * as TCMContracts from "Test/common-util/Contracts/Test";
import { FilterValueItem, sortFilterState } from "Test/common-util/TestResults.Filtering.Common";
import { ConfigurationToolbarActionsCreator } from "Test/ConfigurationToolbar/Actions/ConfigurationToolbarActionsCreator";
import { FilterActionsCreator } from "Test/ConfigurationToolbar/Actions/FilterActionsCreator";
import * as Resources from "Test/ConfigurationToolbar/Resources";
import { ConfigurationToolbarStore, IConfigurationToolbarState } from "Test/ConfigurationToolbar/Store/ConfigurationToolbarStore";
import { FilterStore } from "Test/ConfigurationToolbar/Store/FilterStore";
import { LabelledContextualMenu } from "Test/LabelledContextualMenu/LabelledContextualMenu";
import { FilterMetadataActionsCreator } from "Test/MetadataHandler/Actions/FilterMetadataActionsCreator";
import { FilterMetadataStore } from "Test/MetadataHandler/Store/FilterMetadataStore";
import { FilterActionHub } from "Test/TestResultsFilter/Actions/FilterActionHub";
import { TestResultsFilter } from "Test/TestResultsFilter/TestResultsFilter";
import { ITestResultsFilterField, TestResultsFilterFieldType } from "Test/TestResultsFilter/Types";
import { format } from "VSS/Core/Util/String";
import { Components, css, IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { Tooltip } from "VSSUI/TooltipEx";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";

export interface IConfigurationToolbarProps extends IVssComponentProperties {
    store: ConfigurationToolbarStore;
    actionCreator: ConfigurationToolbarActionsCreator;
    testReportContext: CommonTypes.ITestReportContext;
    onConfigurationChange?: (newConfValues: CommonTypes.IReportConfiguration) => void;
    isTestInsightsView: boolean;
    testContext: CommonTypes.ITestContext;
    filterMetadataActionCreator: FilterMetadataActionsCreator;
    filterMetadataStore: FilterMetadataStore;
    onBackNavigation?: () => void;
}

export class ConfigurationToolbar extends VssComponent<IConfigurationToolbarProps, IConfigurationToolbarState> {
    private _reportConfigurationDefinition: Definitions.ReportConfigurationDefinitions;
    private _filterStore: FilterStore;
    private _filterActionsCreator: FilterActionsCreator;
    private _filterActionHub: FilterActionHub;
    private _emptyString: string = "";

    constructor(props: IConfigurationToolbarProps, context: IVssComponentContext) {
        super(props, context);

        this._createFilterStoreAndActionCreator();
        this._reportConfigurationDefinition = new Definitions.ReportConfigurationDefinitions();
        this.props.store.addChangedListener(this._onStoreUpdate);
        this.state = this.props.store.getState();
    }

    public componentDidMount(): void {
        if (this.props) {
            this.props.store.addChangedListener(this._onStoreUpdate);
            this.setState(this.props.store.getState());
        }
    }

    public componentWillUnmount(): void {
        this.props.store.removeChangedListener(this._onStoreUpdate);
        this._filterStore.dispose();
        this._filterActionsCreator.dispose();
    }

    public render(): JSX.Element {
        const commandBarFarItems: IContextualMenuItem[] = [];
        commandBarFarItems.push(this._getPeriodSelectorMenuItem());

        if (!this.props.isTestInsightsView) {
            commandBarFarItems.push(this._getOutcomeSelectorMenuItem());
            commandBarFarItems.push(this._getGroupBySelectorMenuItem());
        }

        const configuredFilters: FilterState = this.props.store.getState().reportConfigurationValues.configuredFilters;
        commandBarFarItems.push(this._getFiltersToggleButton(configuredFilters));

        const menuItems: IContextualMenuItem[] = [];

        if (this.props.isTestInsightsView) {
            menuItems.push({
                className: css("configuration-flex-item"),
                key: "test-back",
                onRender: this._backButtonRenderer
            });
        }

        return (
            <div className="testresults-analytics-report-view-configurationtoolbar">
                <div className="flex">
                    {this.props.isTestInsightsView && (
                        <div className="testinsights-configuration-item flex">
                            <Tooltip text={this.props.testContext.testName} overflowOnly={true}>
                                <Label className="text-ellipsis">{this.props.testContext.testName}</Label>
                            </Tooltip>
                            <div className="testinsights-configuration-separator" />
                        </div>
                    )}
                    <CommandBar className="configuration-commandbar flex-grow" items={menuItems} farItems={commandBarFarItems} />
                </div>
                <div className="configuration-grid-separator" />
                {this.state.isFilterBarVisible && (
                    <TestResultsFilter
                        fields={this._getFilterFieldValues()}
                        store={this._filterStore}
                        actionCreator={this._filterActionsCreator}
                        filterUpdatedCallback={this._onConfiguredFiltersChange}
                        initialFilterState={configuredFilters}
                    />
                )}
            </div>
        );
    }

    private _backButtonRenderer = (item: IContextualMenuItem) => {
        return (
            <Button
                className="testinsights-configuration-backButton"
                text={Resources.BackText}
                iconProps={{ iconName: "RevToggleKey" } as IIconProps}
                onClick={this.props.onBackNavigation}
                ariaLabel={Resources.BackText}
                role="menuitem"
                ariaPosInSet={1}
                ariaSetSize={3}
            />
        );
    };

    private _getPeriodSelectorMenuItem(): IContextualMenuItem {
        return {
            className: "configuration-flex-item",
            key: "period_selector",
            name: "period_selector",
            onRender: this._periodSelectorRenderer
        };
    }

    private _getOutcomeSelectorMenuItem(): IContextualMenuItem {
        return {
            className: "configuration-flex-item",
            key: "outcome_selector",
            name: "outcome_selector",
            onRender: this._outcomeSelectorRenderer
        };
    }

    private _getGroupBySelectorMenuItem(): IContextualMenuItem {
        return {
            className: "configuration-flex-item",
            key: "groupby_selector",
            name: "groupby_selector",
            onRender: this._groupBySelectorRenderer
        };
    }

    private _getFiltersToggleButton(configuredFilter: FilterState): IContextualMenuItem {
        return {
            className: this.state.isFilterBarVisible ? "filter-toggle-button-on" : "filter-toggle-button",
            key: "filter-button",
            iconProps: { iconName: configuredFilter && Object.keys(configuredFilter).length > 0 ? "FilterSolid" : "Filter" },
            onClick: () => {
                // publish telemetry when user clicks on filter bar icon to open it
                if(!this.state.isFilterBarVisible){
                    this.publishPipelineAnalyticsHeaderTelemetry("FilterBar-Toggle");
                }
                this.props.actionCreator.onToggleFilter();
                
            },
            ariaLabel: this.state.isFilterBarVisible ? Resources.HideFilterBarTitle : Resources.ShowFilterBarTitle
        };
    }

    private _getFilterFieldValues(): ITestResultsFilterField[] {
        const filterFieldValues: ITestResultsFilterField[] = [];
        const configuredFilters: FilterState = this.props.store.getState().reportConfigurationValues.configuredFilters || {};

        if (this.props.testReportContext.contextType === TCMContracts.TestResultsContextType.Build) {
            filterFieldValues.push({
                displayType: TestResultsFilterFieldType.CheckboxList,
                fieldName: CommonTypes.Filter.Workflow,
                placeholder: Resources.Workflow
            });
        }

        filterFieldValues.push(
            {
                displayType: TestResultsFilterFieldType.CheckboxList,
                fieldName: CommonTypes.Filter.Branch,
                placeholder: Resources.BranchText,
                noItemsText: Resources.FilterNoBranch
            },
            {
                displayType: TestResultsFilterFieldType.CheckboxList,
                fieldName: CommonTypes.Filter.Environment,
                placeholder: Resources.Stage,
                noItemsText: Resources.FilterNoStage,
                disabled:
                    this.props.testReportContext.contextType === TCMContracts.TestResultsContextType.Build &&
                    this.doesWorkflowFilterContainOnlyBuild(configuredFilters)
            },
            {
                displayType: TestResultsFilterFieldType.CheckboxList,
                fieldName: CommonTypes.Filter.TestRun,
                placeholder: Resources.FilterByTestRun,
                noItemsText: Resources.FilterNoTestRun
            }
        );

        if (this.props.isTestInsightsView) {
            filterFieldValues.push({
                displayType: TestResultsFilterFieldType.CheckboxList,
                fieldName: CommonTypes.Filter.Outcome,
                placeholder: Resources.FilterByOutcome
            });
        } else {
            filterFieldValues.push(
                {
                    displayType: TestResultsFilterFieldType.CheckboxList,
                    fieldName: CommonTypes.Filter.Container,
                    placeholder: Resources.FilterByTestFile,
                    noItemsText: Resources.FilterNoTestFile
                },
                {
                    displayType: TestResultsFilterFieldType.CheckboxList,
                    fieldName: CommonTypes.Filter.Owner,
                    placeholder: Resources.FilterByOwner,
                    noItemsText: Resources.FilterNoOwner
                }
            );
        }
        return filterFieldValues;
    }

    private _createFilterStoreAndActionCreator() {
        this._filterActionHub = new FilterActionHub();
        this._filterStore = new FilterStore(this._filterActionHub);
        this._filterActionsCreator = new FilterActionsCreator({
            testReportContext: this.props.testReportContext,
            actionsHub: this._filterActionHub,
            filterMetadataActionCreator: this.props.filterMetadataActionCreator,
            filterMetadataStore: this.props.filterMetadataStore
        });
    }

    private _periodSelectorRenderer = (item: IContextualMenuItem) => {
        const configurationValues = this.state.reportConfigurationValues;
        const periodOptions = this._reportConfigurationDefinition.getPeriodConfigurationProps().options;
        return (
            <Tooltip text={Resources.PeriodLabel}>
                <LabelledContextualMenu
                    optionsCssClassName="configuration-selector-options"
                    iconProps={{ iconName: "Calendar" } as IIconProps}
                    options={this._convertToContextualMenuItemArray(periodOptions)}
                    selectedOptionsText={periodOptions[configurationValues.period] || this._emptyString}
                    onChange={this._onPeriodChanged}
                    contextualMenuAriaLabel={format(
                        Resources.PeriodSelectedAriaLabel,
                        periodOptions[configurationValues.period] || this._emptyString
                    )}
                    ariaPosinset={this.props.isTestInsightsView ? 2 : 1}
                    ariaSetsize={this.props.isTestInsightsView ? 3 : 4}
                />
            </Tooltip>
        );
    };

    private _outcomeSelectorRenderer = (item: IContextualMenuItem) => {
        const configurationValues = this.state.reportConfigurationValues;
        const outcomeOptions = this._reportConfigurationDefinition.getOutcomeConfigurationProps().options;

        let contextualMenuAriaLabel = this._emptyString;
        let selectedOptionText: string = this._emptyString;
        if (configurationValues.outcomes.length > 1) {
            selectedOptionText = format(
                Resources.MultipleOptionsSelectedText,
                outcomeOptions[configurationValues.outcomes[0]],
                configurationValues.outcomes.length - 1
            );
            contextualMenuAriaLabel = format(
                Resources.MultipleOutcomeSelectedAriaLabel,
                outcomeOptions[configurationValues.outcomes[0]],
                configurationValues.outcomes.length - 1
            );
        } else if (configurationValues.outcomes.length === 1) {
            selectedOptionText = outcomeOptions[configurationValues.outcomes[0]];
            contextualMenuAriaLabel = format(Resources.OutcomeSelectedAriaLabel, outcomeOptions[configurationValues.outcomes[0]]);
        } else {
            selectedOptionText = Resources.AllText;
            contextualMenuAriaLabel = format(Resources.OutcomeSelectedAriaLabel, Resources.AllOutcomesText);
        }

        return (
            <Tooltip text={Resources.OutcomeFilterText}>
                <LabelledContextualMenu
                    optionsCssClassName="configuration-selector-options"
                    iconProps={{ className: "bowtie-icon icon bowtie-test-fill" } as IIconProps}
                    options={this._convertToContextualMenuItemArrayWithMultiSelect(outcomeOptions, configurationValues.outcomes as number[])}
                    selectedOptionsText={selectedOptionText}
                    onChange={this._onOutcomeChanged}
                    contextualMenuAriaLabel={contextualMenuAriaLabel}
                    ariaPosinset={2}
                    ariaSetsize={4}
                />
            </Tooltip>
        );
    };

    private _groupBySelectorRenderer = (item: IContextualMenuItem) => {
        const configurationValues = this.state.reportConfigurationValues;

        const groupByOptions: { [key: number]: string } = {};
        const allGroupByOptions = this._reportConfigurationDefinition.getGroupByConfigurationProps().options as string[];
        Object.keys(allGroupByOptions).forEach(k => {
            if (
                !(
                    k === CommonTypes.GroupBy.Environment.toString() &&
                    this.props.testReportContext.contextType !== TCMContracts.TestResultsContextType.Release
                )
            ) {
                groupByOptions[Number(k)] = allGroupByOptions[Number(k)];
            }
        });

        return (
            <LabelledContextualMenu
                optionsCssClassName="configuration-selector-options"
                iconProps={{ className: "bowtie-icon icon bowtie-group-rows" } as IIconProps}
                options={this._convertToContextualMenuItemArray(groupByOptions)}
                selectedOptionsText={groupByOptions[configurationValues.groupBy] || this._emptyString}
                onChange={this._onGroupByChanged}
                contextualMenuAriaLabel={format(Resources.GroupBySelectedAriaLabel, groupByOptions[configurationValues.groupBy] || this._emptyString)}
                tooltipProps={{ text: Resources.GroupByText }}
                ariaPosinset={3}
                ariaSetsize={4}
            />
        );
    };

    private _onPeriodChanged = (ev: React.MouseEvent<HTMLElement>, item: IContextualMenuItem) => {
        const newConfValues = { ...this.state.reportConfigurationValues };
        newConfValues.period = Number(item.key);

        // publish telemetry when period value changes
        this.publishPipelineAnalyticsHeaderTelemetry("Dropdown-PeriodPicker")
        this._onChanged(newConfValues);
    };

    private _onOutcomeChanged = (ev: React.MouseEvent<HTMLElement>, item: IContextualMenuItem) => {
        // This will let dropdown not dismiss.
        ev.preventDefault();

        const newConfValues = { ...this.state.reportConfigurationValues };

        let newSelectedOutcomes: CommonTypes.TestOutcome[] = [...newConfValues.outcomes];
        newSelectedOutcomes = newSelectedOutcomes.filter(o => o.toString() !== item.key);

        if (newSelectedOutcomes.length === newConfValues.outcomes.length) {
            newSelectedOutcomes.push(Number(item.key));
        }

        newConfValues.outcomes = newSelectedOutcomes.sort();

        // publish telemetry when test outcome value changes
        this.publishPipelineAnalyticsHeaderTelemetry("Dropdown-TestOutcome");
        this._onChanged(newConfValues);
    };

    private _onGroupByChanged = (ev: React.MouseEvent<HTMLElement>, item: IContextualMenuItem) => {
        const newConfValues = { ...this.state.reportConfigurationValues };
        newConfValues.groupBy = Number(item.key);

        // publish telemetry when group by value changes
        this.publishPipelineAnalyticsHeaderTelemetry("Dropdown-GroupBy")
        this._onChanged(newConfValues);
    };

    private _onConfiguredFiltersChange = (configuredFilters: FilterState) => {
        sortFilterState(configuredFilters);

        const newConfValues = { ...this.state.reportConfigurationValues };
        newConfValues.configuredFilters = { ...configuredFilters };

        if (this.doesWorkflowFilterContainOnlyBuild(newConfValues.configuredFilters)) {
            // When workflow in Build then no environment filter to be shown so remove all environment filters
            delete newConfValues.configuredFilters[CommonTypes.Filter.Environment];
        }

        this._onChanged(newConfValues);
    };

    private _onChanged = (newConfValues: CommonTypes.IReportConfiguration) => {
        this.props.actionCreator.updateConfigurationValues(this.props.testReportContext, newConfValues);
        if (this.props.onConfigurationChange) {
            this.props.onConfigurationChange(newConfValues);
        }
    };

    private _convertToContextualMenuItemArray(options: { [key: number]: string }): IContextualMenuItem[] {
        return Object.keys(options).map(k => ({ key: k, name: options[Number(k)] } as IContextualMenuItem));
    }

    private _convertToContextualMenuItemArrayWithMultiSelect(options: { [key: number]: string }, selectedKeys: number[]): IContextualMenuItem[] {
        const selectedKeysMap: { [key: number]: boolean } = {};
        selectedKeys.forEach(k => (selectedKeysMap[k] = true));

        return Object.keys(options).map(
            k => ({ key: k, name: options[Number(k)], canCheck: true, checked: selectedKeysMap[Number(k)] } as IContextualMenuItem)
        );
    }

    private doesWorkflowFilterContainOnlyBuild(filters: FilterState): boolean {
        return (
            filters &&
            filters[CommonTypes.Filter.Workflow] &&
            filters[CommonTypes.Filter.Workflow].values &&
            filters[CommonTypes.Filter.Workflow].values.length === 1 &&
            filters[CommonTypes.Filter.Workflow].values
                .map((v: FilterValueItem) => v.value as string)
                .indexOf(CommonTypes.Workflow.Build.toString()) >= 0
        );
    }

    private _onStoreUpdate = () => {
        this.setState(this.props.store.getState());
    };

    private publishPipelineAnalyticsHeaderTelemetry = (source: string): void => {
        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent("PipelineAnalytics", "PipelineAnalyticsHeader", {
            parentPageName: "TestManagement",
            source
        })
    }
}

Components.add("configuration-toolbar", ConfigurationToolbar);
